import { useEffect, useRef, useState } from 'react'
import { useScrollIntoViewOnMount } from '../../../shared/labs/useScrollIntoView'

// Fallback answer entry, shown UNDER the transcript after commit. A free-text word box
// (cryptic answers are words/phrases, not expressions), with the enumeration shown as a
// length hint. Enter submits; the "I didn't solve it" button records a give-up.
export const CrypticAnswer = ({
  enumeration,
  suggested,
  onSubmit,
  onPass,
}: {
  enumeration: string
  suggested?: string | null // answer detected in the spoken transcript; pre-fills the box
  onSubmit: (answer: string) => void
  onPass: () => void
}) => {
  const [value, setValue] = useState(suggested || '')
  const inputRef = useRef<HTMLInputElement | null>(null)
  const scrollRef = useScrollIntoViewOnMount<HTMLDivElement>() // keep the "I didn't solve it" button clear of the ad

  useEffect(() => {
    // preventScroll so the focus doesn't fight the settling-scroll that positions the whole section
    inputRef.current?.focus({ preventScroll: true })
    inputRef.current?.select()
  }, [])

  const submit = () => {
    if (value.trim()) onSubmit(value)
  }

  return (
    <div ref={scrollRef} className="text-center">
      <p className="text-sm font-medium text-gray-700">
        {suggested ? 'We heard your answer — check it’s right and submit.' : 'To record your answer, type it below.'}
      </p>
      <p className="mt-0.5 text-xs text-gray-400">Or if you didn’t crack it, that’s fine — tell us below.</p>

      <div className="mt-3 flex items-stretch gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          placeholder={`Your answer ${enumeration}`}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-lg uppercase tracking-wide text-gray-800 focus:border-[#820458] focus:outline-none"
        />
        <button
          type="button"
          disabled={value.trim().length === 0}
          onClick={submit}
          className="rounded-md bg-[#398874] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          Submit
        </button>
      </div>

      <button
        type="button"
        onClick={onPass}
        className="mt-4 w-full rounded-lg border-2 border-gray-300 py-4 text-base font-bold tracking-wide text-gray-500 hover:bg-gray-50"
      >
        I didn’t solve it
      </button>
    </div>
  )
}
